import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription, filter } from 'rxjs';
import { Device } from 'src/app/model/device';
import { DeviceInfo } from 'src/app/model/device-info';
import { DeviceStatus } from 'src/app/model/device-status';
import { IOPayload } from 'src/app/model/io-payload';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDevice, selectCurrentDeviceInfo, selectCurrentDeviceStatus, selectInputEdited, selectOutputEdited } from 'src/app/store/device/device.selector';
import { EditInputDialogComponent } from '../edit-input-dialog/edit-input.dialog.component';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { EditOutputDialogComponent } from '../edit-output-dialog/edit-output.dialog.component';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';

@Component({
	selector: 'app-virtual-device',
	templateUrl: './virtual-device.component.html',
	providers: [ConfirmationService, DialogService]
})
export class VirtualDeviceComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	inputEdited$: Observable<boolean | undefined>;
	inputEditedSub: Subscription | undefined;
	outputEdited$: Observable<boolean | undefined>;
	outputEditedSub: Subscription | undefined;

	owned: boolean;
	device: Device;
	inputs: IOPayload[];
	outputs: IOPayload[];
	deviceStatus: DeviceStatus;

	dialogSub: Subscription;

	ref: DynamicDialogRef | undefined;

   screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void{
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.inputEdited$ = this.store.select(selectInputEdited);
		this.outputEdited$ = this.store.select(selectOutputEdited);
	}

	ngOnInit(): void {
		this.deviceSub = this.device$.pipe(filter(d => !!d)) // currentDevice === undefined when deleting
		.subscribe(device => {
			this.owned = device.CurrentlyOwned;
			this.device = device;
		});
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(di => !!di))
			.subscribe(deviceInfo => {
				this.inputs = deviceInfo.Terminals.filter(t => t.InputId !== undefined);
				this.outputs = deviceInfo.Terminals.filter(t => t.OutputId !== undefined);
			});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			this.deviceStatus = deviceStatus;
		});
		this.inputEditedSub = this.inputEdited$?.pipe(filter(edit => !!edit))
		  .subscribe(() => {
			this.store.dispatch(DeviceActions.updateCurrentDeviceStatus());
		});
		this.outputEditedSub = this.outputEdited$?.pipe(filter(edit => !!edit))
		  .subscribe(() => {
			this.store.dispatch(DeviceActions.updateCurrentDeviceStatus());
		});

		this.onResize();
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.inputEditedSub?.unsubscribe();
		this.outputEditedSub?.unsubscribe();
	}

	inputActive(input: IOPayload): boolean {
		return this.deviceStatus.Inputs.get(input.InputId) === input.StateLabelActive;
	}

	outputActive(output: IOPayload): boolean {
		return this.deviceStatus.Outputs.get(output.OutputId) === output.StateLabelActive;
	}

	editInput(terminal: IOPayload): void {
		this.ref = this.dialogService.open(EditInputDialogComponent, {
			header: this.translate.instant('EDIT_MULTI_IO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				terminal,
				device: this.device,
			},
		});
	}

	editOutput(terminal: IOPayload): void {
		this.ref = this.dialogService.open(EditOutputDialogComponent, {
			header: this.translate.instant('EDIT_MULTI_IO'),
			width: '70vw',
			breakpoints: { '1600px' : '98vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				terminal,
				device: this.device,
			},
		});
	}

	outputBistable(output: IOPayload): boolean {
		// eslint-disable-next-line no-bitwise
		return !!(output.CommandType & 1);
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}
}
